// Card compartilhável (PNG) do indicador de Faturamento (PO1-3632): volume
// auditado do recorte (CT-es, faturas e frete, mês a mês), desenhado à mão em
// <canvas> nativo, no padrão do Emissoes/shareableCard.js (sem dependência
// DOM→imagem). `numPt` e `quebrarTexto` são cópias do card de Emissões, de
// propósito: a história não refatora o card de carbono.
//
// Formatos: retrato 1080×1350 (padrão) e quadrado 1080×1080.
// Temas: claro (fundo branco, azuis do card manual) e escuro (paleta do Hub).
// O logo é carregado (Image.onload) ANTES de desenhar, para o card nunca sair sem ele.
import logoClaro from "../../../../assets/img/logo.png";
import logoEscuro from "../../../../assets/img/logo2.png";

export const FORMATOS = {
  retrato: { largura: 1080, altura: 1350, rotulo: "Retrato 1080×1350" },
  quadrado: { largura: 1080, altura: 1080, rotulo: "Quadrado 1080×1080" },
};

export const TEMAS = {
  claro: {
    rotulo: "Claro",
    fundo: "#ffffff",
    barra: "#1f5fa8",
    titulo: "#0b3a6e",
    destaque: "#1f5fa8",
    texto: "#1f2937",
    suave: "#64748b",
    linha: "rgba(11, 58, 110, 0.14)",
    cabecalhoTabela: "#eef4fb",
    zebra: "rgba(31, 95, 168, 0.04)",
    logo: logoClaro,
    logoTexto: null,
  },
  escuro: {
    rotulo: "Escuro",
    fundo: "#1b243b",
    barra: "#1d8cf8",
    titulo: "#ffffff",
    destaque: "#1d8cf8",
    texto: "#e5ecf8",
    suave: "#9aa5c0",
    linha: "rgba(255, 255, 255, 0.12)",
    cabecalhoTabela: "rgba(255, 255, 255, 0.06)",
    zebra: "rgba(255, 255, 255, 0.03)",
    logo: logoEscuro,
    // logo2.png é só o símbolo: o nome vai em branco ao lado.
    logoTexto: "Everlog",
  },
};

export const MAX_MESES_TABELA = 6;
export const RODAPE_FONTE = "Fonte: Everlog Auditoria de Fretes · por data de expedição da NF · faturas canceladas excluídas";

const PAD = 80;
const fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

export const numPt = (n, frac = 0) =>
  Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: frac, maximumFractionDigits: frac });

// R$ 41,37 mi · R$ 980,5 mil · R$ 3,3 mil · R$ 412,30 (a partir de mil, sempre
// abreviado, para a coluna da tabela não misturar "R$ 3.270,00" com "mil").
export function brlAbrev(v) {
  const n = Number(v || 0);
  const abs = Math.abs(n);
  if (abs >= 1e9) return `R$ ${numPt(n / 1e9, 2)} bi`;
  if (abs >= 1e6) return `R$ ${numPt(n / 1e6, 2)} mi`;
  if (abs >= 1e3) return `R$ ${numPt(n / 1e3, 1)} mil`;
  return `R$ ${numPt(n, 2)}`;
}

export function quebrarTexto(ctx, texto, maxLargura) {
  const palavras = String(texto || "").split(" ");
  const linhas = [];
  let atual = "";
  palavras.forEach((p) => {
    const teste = atual ? `${atual} ${p}` : p;
    if (ctx.measureText(teste).width > maxLargura && atual) {
      linhas.push(atual);
      atual = p;
    } else {
      atual = teste;
    }
  });
  if (atual) linhas.push(atual);
  return linhas;
}

// "jul a set/2026" | "nov/2025 a fev/2026" | "set/2026"
export function rotuloPeriodo(de, ate) {
  const p = (iso) => {
    const m = /^(\d{4})-(\d{2})/.exec(String(iso || ""));
    return m ? { ano: Number(m[1]), mes: Number(m[2]) } : null;
  };
  const a = p(de);
  const b = p(ate);
  if (!a || !b) return "—";
  if (a.ano === b.ano && a.mes === b.mes) return `${MESES[a.mes - 1]}/${a.ano}`;
  if (a.ano === b.ano) return `${MESES[a.mes - 1]} a ${MESES[b.mes - 1]}/${a.ano}`;
  return `${MESES[a.mes - 1]}/${a.ano} a ${MESES[b.mes - 1]}/${b.ano}`;
}

export const rotuloMes = (mes) => {
  const m = /^(\d{4})-(\d{2})/.exec(String(mes || ""));
  return m ? `${MESES[Number(m[2]) - 1]}/${m[1]}` : String(mes);
};

// Tabela mostra até 6 meses (os últimos); os KPIs seguem com o total.
export function mesesDaTabela(meses) {
  const lista = Array.isArray(meses) ? meses : [];
  if (lista.length <= MAX_MESES_TABELA) return { linhas: lista, truncado: false, total: lista.length };
  return { linhas: lista.slice(lista.length - MAX_MESES_TABELA), truncado: true, total: lista.length };
}

function carregarImagem(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Não foi possível carregar o logo do card."));
    img.src = src;
  });
}

function truncar(ctx, texto, maxLargura) {
  let t = String(texto || "");
  if (ctx.measureText(t).width <= maxLargura) return t;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxLargura) t = t.slice(0, -1);
  return `${t}…`;
}

// dados: { embarcador, de, ate, filtrosLabel?, kpis: { ctes, faturas, frete }, meses: [{ mes, ctes, faturas, valorFrete }] }
// opcoes: { formato: 'retrato'|'quadrado', tema: 'claro'|'escuro' }
// Resolve com { blob, truncado } (truncado = a tabela mostrou só os 6 últimos meses).
export async function desenharCard(dados, opcoes = {}) {
  const formato = FORMATOS[opcoes.formato] || FORMATOS.retrato;
  const tema = TEMAS[opcoes.tema] || TEMAS.claro;
  const quadrado = formato.altura === formato.largura;
  const L = formato.largura;
  const H = formato.altura;
  const larguraUtil = L - PAD * 2;

  const logo = await carregarImagem(tema.logo);

  const canvas = document.createElement("canvas");
  canvas.width = L;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.textBaseline = "alphabetic";

  // Fundo + barra.
  ctx.fillStyle = tema.fundo;
  ctx.fillRect(0, 0, L, H);
  ctx.fillStyle = tema.barra;
  ctx.fillRect(0, 0, L, 14);

  // Logo (altura fixa, largura proporcional) e, no escuro, o nome ao lado.
  const alturaLogo = 64;
  const larguraLogo = Math.round((logo.width / logo.height) * alturaLogo);
  const yLogo = 60;
  ctx.drawImage(logo, PAD, yLogo, larguraLogo, alturaLogo);
  if (tema.logoTexto) {
    ctx.fillStyle = tema.titulo;
    ctx.font = `800 40px ${fontFamily}`;
    ctx.fillText(tema.logoTexto, PAD + larguraLogo + 18, yLogo + 48);
  }

  // Nome do embarcador, à direita, na linha do logo.
  ctx.fillStyle = tema.suave;
  ctx.font = `600 26px ${fontFamily}`;
  ctx.textAlign = "right";
  ctx.fillText(truncar(ctx, dados.embarcador || "", larguraUtil - larguraLogo - 160), L - PAD, yLogo + 44);
  ctx.textAlign = "left";

  // Título, período e filtros.
  let y = quadrado ? 220 : 240;
  ctx.fillStyle = tema.titulo;
  ctx.font = `800 ${quadrado ? 56 : 64}px ${fontFamily}`;
  ctx.fillText("Volume auditado", PAD, y);
  y += quadrado ? 50 : 56;
  ctx.fillStyle = tema.destaque;
  ctx.font = `600 ${quadrado ? 30 : 34}px ${fontFamily}`;
  ctx.fillText(rotuloPeriodo(dados.de, dados.ate), PAD, y);
  if (dados.filtrosLabel) {
    y += quadrado ? 36 : 40;
    ctx.fillStyle = tema.suave;
    ctx.font = `500 ${quadrado ? 22 : 24}px ${fontFamily}`;
    ctx.fillText(truncar(ctx, dados.filtrosLabel, larguraUtil), PAD, y);
  }
  y += quadrado ? 34 : 40;

  // Divisor.
  ctx.strokeStyle = tema.linha;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(PAD, y);
  ctx.lineTo(L - PAD, y);
  ctx.stroke();

  // KPIs (3 colunas).
  const k = dados.kpis || {};
  const nMeses = Array.isArray(dados.meses) && dados.meses.length ? dados.meses.length : 1;
  const porMes = Number(k.frete || 0) / nMeses;
  const kpis = [
    { rotulo: "CT-es", valor: numPt(k.ctes), ajuda: "documentos auditados" },
    { rotulo: "Faturas", valor: numPt(k.faturas), ajuda: "faturas conferidas" },
    { rotulo: "Frete", valor: brlAbrev(k.frete), ajuda: `~${brlAbrev(porMes)} por mês` },
  ];
  const colunaLargura = larguraUtil / 3;
  const yKpiRotulo = y + (quadrado ? 56 : 66);
  const yKpiValor = yKpiRotulo + (quadrado ? 60 : 70);
  const yKpiAjuda = yKpiValor + (quadrado ? 34 : 38);
  kpis.forEach((kpi, i) => {
    const x = PAD + i * colunaLargura;
    ctx.fillStyle = tema.suave;
    ctx.font = `700 ${quadrado ? 22 : 24}px ${fontFamily}`;
    ctx.fillText(kpi.rotulo.toUpperCase(), x, yKpiRotulo);
    ctx.fillStyle = tema.destaque;
    // O número nunca é cortado ("R$ 44,62 mi"): a fonte diminui até caber.
    let tamanho = quadrado ? 52 : 60;
    ctx.font = `800 ${tamanho}px ${fontFamily}`;
    while (tamanho > 28 && ctx.measureText(kpi.valor).width > colunaLargura - 24) {
      tamanho -= 2;
      ctx.font = `800 ${tamanho}px ${fontFamily}`;
    }
    ctx.fillText(kpi.valor, x, yKpiValor);
    ctx.fillStyle = tema.texto;
    ctx.font = `500 ${quadrado ? 21 : 23}px ${fontFamily}`;
    ctx.fillText(truncar(ctx, kpi.ajuda, colunaLargura - 24), x, yKpiAjuda);
  });

  // Tabela mês a mês.
  const tabela = mesesDaTabela(dados.meses);
  let yTab = yKpiAjuda + (quadrado ? 56 : 70);
  const alturaLinha = quadrado ? 48 : 56;
  const colunas = [
    { rotulo: "Mês", x: PAD, alinhar: "left" },
    { rotulo: "CT-es", x: PAD + larguraUtil * 0.5, alinhar: "right" },
    { rotulo: "Faturas", x: PAD + larguraUtil * 0.72, alinhar: "right" },
    { rotulo: "Frete", x: L - PAD, alinhar: "right" },
  ];
  ctx.fillStyle = tema.cabecalhoTabela;
  ctx.fillRect(PAD - 12, yTab - alturaLinha + 14, larguraUtil + 24, alturaLinha);
  ctx.fillStyle = tema.suave;
  ctx.font = `700 ${quadrado ? 20 : 22}px ${fontFamily}`;
  colunas.forEach((c) => {
    ctx.textAlign = c.alinhar;
    ctx.fillText(c.rotulo.toUpperCase(), c.x, yTab);
  });
  yTab += alturaLinha;
  ctx.font = `500 ${quadrado ? 24 : 26}px ${fontFamily}`;
  if (tabela.linhas.length === 0) {
    ctx.textAlign = "left";
    ctx.fillStyle = tema.suave;
    ctx.fillText("Sem CT-e no recorte.", PAD, yTab);
  }
  tabela.linhas.forEach((m, i) => {
    if (i % 2 === 1) {
      ctx.fillStyle = tema.zebra;
      ctx.fillRect(PAD - 12, yTab - alturaLinha + 14, larguraUtil + 24, alturaLinha);
    }
    ctx.fillStyle = tema.texto;
    ctx.textAlign = "left";
    ctx.fillText(rotuloMes(m.mes), PAD, yTab);
    ctx.textAlign = "right";
    ctx.fillText(numPt(m.ctes), colunas[1].x, yTab);
    ctx.fillText(numPt(m.faturas), colunas[2].x, yTab);
    ctx.fillText(brlAbrev(m.valorFrete), colunas[3].x, yTab);
    yTab += alturaLinha;
  });
  ctx.textAlign = "left";
  if (tabela.truncado) {
    ctx.fillStyle = tema.suave;
    ctx.font = `500 ${quadrado ? 19 : 21}px ${fontFamily}`;
    ctx.fillText(`Tabela com os ${MAX_MESES_TABELA} últimos meses; os totais acima são dos ${tabela.total} meses do recorte.`, PAD, yTab);
  }

  // Rodapé com o critério.
  ctx.fillStyle = tema.suave;
  ctx.font = `400 20px ${fontFamily}`;
  const linhasRodape = quebrarTexto(ctx, RODAPE_FONTE, larguraUtil);
  let yf = H - 60 - (linhasRodape.length - 1) * 26;
  linhasRodape.forEach((linha) => {
    ctx.fillText(linha, PAD, yf);
    yf += 26;
  });

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => {
      if (b) resolve(b);
      else reject(new Error("Falha ao gerar o PNG do card."));
    }, "image/png");
  });
  return { blob, truncado: tabela.truncado, largura: L, altura: H };
}

// card-faturamento-<embarcador>-<de>-a-<ate>.png
export function nomeArquivo(embarcador, de, ate) {
  const slug = String(embarcador || "embarcador")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  return `card-faturamento-${slug || "embarcador"}-${de}-a-${ate}.png`;
}

export function baixarBlob(blob, nome) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = nome;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export const suportaCopiarImagem = () =>
  typeof window !== "undefined" &&
  typeof window.ClipboardItem !== "undefined" &&
  Boolean(navigator.clipboard && typeof navigator.clipboard.write === "function");

// Deve rodar dentro do handler do clique (gesto do usuário).
export async function copiarBlob(blob) {
  if (!suportaCopiarImagem()) throw new Error("Este navegador não permite copiar imagem. Use Baixar PNG.");
  await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
}
